import {put, call, all} from 'redux-saga/effects';
import HolidayCalendar from "../../entities/holidayCalendar";
import * as Actions from "../actions/holidayCalendarActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type HolidayCalendarAction = {
    holidayCalendarId: string;
    holidayCalendar: HolidayCalendar;
    holidayCalendars: HolidayCalendar[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneHolidayCalendar(action: HolidayCalendarAction) {
	const session = tabSession.captureGeneration();
	try {
		const holidayCalendar: HolidayCalendar = yield call(api.findOne, 'holidayCalendar', action.holidayCalendarId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.HOLIDAYCALENDAR_FETCH_ONE_SUCCESS, holidayCalendarId: action.holidayCalendarId, holidayCalendar, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.HOLIDAYCALENDAR_FETCH_ONE_FAILURE, holidayCalendarId: action.holidayCalendarId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {HolidayCalendarAction} action
 */
export function* saveHolidayCalendar(action: HolidayCalendarAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ HOLIDAYCALENDAR_SAVE saga', action.holidayCalendar?.id);

		if (action.holidayCalendar === undefined || action.holidayCalendar === null) {
			console.error('saveHolidayCalendar received a null');
			throw new Error('saveHolidayCalendar received a null');
		}

		if (!(action.holidayCalendar instanceof HolidayCalendar)) {
			action.holidayCalendar = new HolidayCalendar(action.holidayCalendar);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.holidayCalendar.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.holidayCalendar);
			const addResult = yield call(idb.add, 'holidayCalendar', action.holidayCalendar);
			if (addResult.isErr()) {
				console.error(`⮂ saveHolidayCalendar: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertHolidayCalendarSave(action.holidayCalendar, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.HOLIDAYCALENDAR_SAVE_FAILURE, holidayCalendar: action.holidayCalendar, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.HOLIDAYCALENDAR_SAVE, holidayCalendar: action.holidayCalendar, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertHolidayCalendarSave(action.holidayCalendar, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.HOLIDAYCALENDAR_SAVE_FAILURE, holidayCalendar: action.holidayCalendar, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {HolidayCalendarAction} action
 * @param {HolidayCalendar[]} action.holidayCalendars
 */
export function* saveManyHolidayCalendars(action: HolidayCalendarAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ HOLIDAYCALENDAR_SAVE_MANY saga', action.holidayCalendars.map(holidayCalendar => holidayCalendar.id).join(','));

        if (!Array.isArray(action.holidayCalendars)) {
            console.error('saveManyHolidayCalendars expected an array, received', action.holidayCalendars);
            throw new Error('saveManyHolidayCalendars expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveHolidayCalendar), under these queue keys
		const dispatchedAddedOns: number[] = action.holidayCalendars.map(holidayCalendar => holidayCalendar?.addedOn);
		const revertAll = () => action.holidayCalendars.map((holidayCalendar, i) =>
			put(Actions.revertHolidayCalendarSave(holidayCalendar, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: HolidayCalendar[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.holidayCalendars.length; i++) {
				const holidayCalendar = action.holidayCalendars[i];
				holidayCalendar.persisted = false;
				ensureOperationId(holidayCalendar);
				toSave.push(holidayCalendar);
			}

			const addResult = yield call(idb.add, 'holidayCalendar', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyHolidayCalendars: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.HOLIDAYCALENDAR_SAVE_MANY_FAILURE, holidayCalendars: action.holidayCalendars, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.HOLIDAYCALENDAR_SAVE_MANY, holidayCalendars: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.HOLIDAYCALENDAR_SAVE_MANY_FAILURE, holidayCalendars: action.holidayCalendars, status: 500, err});

			console.error(`Error saving ${action.holidayCalendars.length} holidayCalendars to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {HolidayCalendarAction} action
 */
export function* removeHolidayCalendar(action: HolidayCalendarAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.holidayCalendar === undefined || action.holidayCalendar === null || !(action.holidayCalendar instanceof HolidayCalendar)) {
			console.error('removeHolidayCalendar expected type HolidayCalendar, received', action.holidayCalendar);
			reportQueued(false);
			throw new Error('removeHolidayCalendar expected type HolidayCalendar');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.holidayCalendar.status = 'D';
		action.holidayCalendar.addedOn = newHRTimestamp();
		action.holidayCalendar.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.holidayCalendar);
		const addResult = yield call(idb.add, 'holidayCalendar', action.holidayCalendar);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeHolidayCalendar: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.HOLIDAYCALENDAR_REMOVE_FAILURE, holidayCalendar: action.holidayCalendar, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.HOLIDAYCALENDAR_REMOVE, holidayCalendar: action.holidayCalendar});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.HOLIDAYCALENDAR_REMOVE_FAILURE, holidayCalendar: action.holidayCalendar, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this HolidayCalendar.
	*
	* @param {HolidayCalendarAction} action
	*/
export function* restoreHolidayCalendar(action: HolidayCalendarAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreHolidayCalendar expected getCurrentUserId() to return a value');
        return;
    }

	if (action.holidayCalendar === undefined || action.holidayCalendar === null) {
        console.error('restoreHolidayCalendar expected a HolidayCalendar, received null');
        throw new Error('restoreHolidayCalendar expected a HolidayCalendar, received null');
    }
    else if (!(action.holidayCalendar instanceof HolidayCalendar)) action.holidayCalendar = new HolidayCalendar(action.holidayCalendar); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.holidayCalendar.status = 'U';
		action.holidayCalendar.addedOn = newHRTimestamp();
		action.holidayCalendar.addedBy = userId;
		action.holidayCalendar.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.holidayCalendar);
		const addResult = yield call(idb.add, 'holidayCalendar', action.holidayCalendar);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreHolidayCalendar: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.HOLIDAYCALENDAR_RESTORE_FAILURE, holidayCalendar: action.holidayCalendar, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.HOLIDAYCALENDAR_RESTORE, holidayCalendar: action.holidayCalendar});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.HOLIDAYCALENDAR_RESTORE_FAILURE, holidayCalendar: action.holidayCalendar, status: 500, err});

		console.error('Error restoring from database', err);
	}
}